//Hooks
import { get } from 'svelte/store';
//Formating
import { format } from 'svelte-i18n';
//Helper
import {
	isBluTRV,
	isCamera,
	isDiffuser,
	isGen2EM,
	isProThreeEM,
	isShellyPresence,
	isWallDisplay,
} from '../../../interface_helpers';
import { capFirstChar } from '../../../utilities';
import { getVirtualWebHookTypes } from './webhook_virtual_helpers';
import { getComponentByDeviceMode } from '../../local/core/local_action_components_helpers';
import {
	isXT1ComponentDevice,
	isXT1Module,
	resolveXT1ComponentChannel,
	translateTemplateTitle,
} from '../../../xt1_helpers/template_helpers';
import {
	bthomeButtonsConditions,
	emPhasesConditions,
	getPresenceZoneCondition,
	getTransformedWebHookConditions,
	getWebHookConditions,
} from '../conditions/webhook_conditions_helpers';
import { getPresenceComponents } from '../../local/devices/presence_local_actions';
import { isVirtualDevice } from 'src/helpers/virtual_helpers/virtual_components_helpers';
import { getCameraComponents } from '../../local/devices/camera_local_actions';
import { getWebhookEventTitle } from '../view/webhook_event_title_helpers';
import {
	checkInputsMoreThanChannels,
	getComponentInputType,
	getComponents,
	getLnmComponents,
	getVoltmeterComponents,
} from './webhook_components_helpers';
//Stores
import { devices_settings } from 'src/stores/device-runtime/status';
//Configs
import { SLEEPING_TYPES } from 'src/constants';
//Types
import type {
	WebHookConditionOption,
	WebHookEventOption,
	WebHooksParams,
	WebHookSupportedList,
} from 'src/types/requests/settings/webhooks';
import type { WebHookCondition } from '../conditions/webhook_conditions_helpers';
import type { Device } from 'src/types/Device';

export const isXT1VirtualWebhookComponent = (device: Device): boolean =>
	isXT1Module(device.type) &&
	/^(boolean|enum|text|button|number|bthomesensor|bthomedevice):2\d{2}$/.test(device.component);

export const getWebHookEvents = (
	actionProps: WebHooksParams,
	supportedActionsList: WebHookSupportedList,
	device: Device,
): { options: WebHookEventOption[]; index: number } => {
	let options: WebHookEventOption[] = [],
		index = -1,
		inc = 0,
		$format = get(format);
	for (const [event, value] of Object.entries(supportedActionsList.types)) {
		let type = event.match(new RegExp(`^(${actionProps.type})\\.`))?.[1];
		const inputType = actionProps.inputType == 'switch' ? 'toggle' : actionProps.inputType,
			customExprNotSet =
				/^(input|voltmeter)$/.test(actionProps.type) &&
				actionProps.event == event &&
				((/(xtotal|xpercent|xvoltage)/.test(actionProps.condition) && !actionProps.xunit) ||
					(/xfreq/.test(actionProps.condition) && !actionProps.freqXUnit)),
			disabled =
				type == 'input' &&
				actionProps.event == event &&
				!new RegExp(`(${getComponentInputType(actionProps)})`).test(actionProps.event);
		if (disabled) type = event;
		else if (!type || (/measurement/.test(event) && SLEEPING_TYPES.includes(device.type))) {
			continue;
		} else if (/^(rgb|em)$/.test(type)) type += '.';
		else if (/^(input|bthomesensor)$/.test(type)) {
			const _inputType = event.split('.')[1].match(new RegExp(`^(${inputType})`))?.[0];
			if (_inputType) {
				if (/^(input)$/.test(type)) type += '.' + _inputType;
			} else continue;
		}
		if (!event.startsWith(type)) continue;
		let eventTitle = event
			.replace(/(em1|pm1)/, 'em')
			.replace('apower_change', 'active_power_change')
			.replace(/^(cct|rgbcct)$/, 'light');
		if (
			/^(bthome(sensor|device)|button)\./.test(event) &&
			/(_push|rotate_(left|right))/.test(event)
		) {
			eventTitle = `input_${eventTitle
				.replace('_push', 'push')
				.replace(/((bthome(sensor|device))\.)/g, 'button_')
				.replace(/(single)/g, '')}`;
		} else if (event.startsWith('bthomesensor.')) {
			eventTitle = eventTitle.replace(/^bthomesensor/, actionProps.title);
		}
		const condition = getPresenceZoneCondition(actionProps.type, actionProps.condition || '').split(
				' ',
			),
			tokens = value?.attrs?.map((i) => `ev.${i.name}`),
			title = capFirstChar(getWebhookEventTitle(event, eventTitle)),
			conditionOption: WebHookEventOption = {
				title: isXT1Module(device?.type) ? translateTemplateTitle(title) : title,
				event,
				value: inc,
				tokens,
				type: actionProps.type,
				disabled,
				customExprNotSet,
				conditions: [],
				icon: actionProps?.icon,
				config: actionProps?.config,
			};
		if (
			actionProps?.config?.owner &&
			actionProps.type == 'button' &&
			(actionProps.enums || []).find((i) => conditionOption.event == `button.${i.value}`)
		) {
			conditionOption.title = actionProps.title || conditionOption.title;
			options.push(conditionOption);
			return { options, index };
		}
		options.push(conditionOption);
		if (event == actionProps.event) {
			index = inc;
		}
		++inc;
		if (!value?.attrs) continue;
		value.attrs.forEach((attr) => {
			const type = actionProps.type?.split('|')?.[0],
				conditionEvent = `ev.${attr.name}`,
				isRawExpression = attr.type === 'object',
				pro3EMPhases = type == 'em' && attr.name == 'phase',
				conditions: WebHookCondition[] = getWebHookConditions(attr.type, type, attr.name);
			let conditionsIndex = -1,
				conditionValue = pro3EMPhases ? '"a"' : attr.type == 'string' ? '' : 0,
				conditionCurrIndex = condition.indexOf(conditionEvent);
			if (isRawExpression) {
				conditionValue = '';
				if (event == actionProps.event && actionProps.condition) {
					conditionValue = actionProps.condition;
					conditionsIndex = 0;
				}
			} else if (
				condition.length &&
				event == actionProps.event &&
				conditionCurrIndex >= 0 &&
				conditions.find((i) => condition[conditionCurrIndex + 1].startsWith(i.value))
			) {
				conditionValue = condition[conditionCurrIndex + 2];
				conditionsIndex = conditions.findIndex((i) =>
					condition[conditionCurrIndex + 1].startsWith(i.value),
				);
			} else if (actionProps.enums?.[0]?.value != null) {
				conditionValue = actionProps.enums[0].value;
			} else if (type == 'cury' && attr.name == 'slot') {
				conditionValue = '"left"';
			} else if (attr.name == 'illumination') {
				conditionValue = '"dark"';
			} else if (type == 'presencezone' && attr.name == 'value') {
				conditionValue = 'true';
			}
			const option: WebHookConditionOption = {
				title: '',
				event,
				value: inc,
				conditions,
				conditionsIndex,
				conditionEvent,
				conditionValue,
				tokens,
				phases: pro3EMPhases ? emPhasesConditions(event, device) : null,
				buttons: attr?.buttons ? bthomeButtonsConditions(attr.buttons) : null,
				type,
				enums: actionProps.enums,
				attrName: attr.name,
				label: capFirstChar(
					$format(attr.desc, {
						default: attr.defaultDesc || attr.desc.replace(/_/g, ' '),
					}),
				),
				isRawExpression,
			};
			if (conditionOption.conditions.length > 0) {
				if (/^(temperature|blutrv.temperature)/i.test(event)) {
					//PlusH&T and BluTRV
					if (actionProps.unit == 'C') {
						option.conditionEvent = conditionOption.conditions[0].conditionEvent;
						option.attrName = conditionOption.conditions[0].attrName;
						option.label = conditionOption.conditions[0].label;
					} else if (actionProps.unit == 'F') {
						conditionOption.conditions[0].conditionEvent = option.conditionEvent;
						conditionOption.conditions[0].attrName = option.attrName;
						conditionOption.conditions[0].label = option.label;
					}
					if (condition?.[0] == `ev.${attr.name}`) {
						conditionOption.conditions[0] = option;
					}
				} else if (/^(xtotal|xfreq|xpercent|xvoltage)$/.test(attr.name)) {
					const basicConditions = conditionOption.conditions[0];
					const basicConditionsLength = basicConditions.conditions.length;
					if (conditionValue) {
						basicConditions.conditionValue = conditionValue;
					}
					if (conditionsIndex >= 0) {
						basicConditions.conditionsIndex = conditionsIndex + basicConditionsLength;
					}
					basicConditions.conditions = [
						...basicConditions.conditions,
						...getTransformedWebHookConditions(
							conditionEvent,
							option.label,
							basicConditionsLength,
						),
					];
				} else {
					conditionOption.conditions.push(option);
				}
			} else {
				conditionOption.conditions.push(option);
			}
		});
	}
	return { options, index };
};

export const getWebHookTypes = (device: Device, plusUniWebHook: boolean = false): WebHooksParams => {
	if (plusUniWebHook) {
		return { cid: 2, type: 'input', inputType: 'count|freq', unit: '' };
	}
	if (isXT1VirtualWebhookComponent(device)) {
		const component = getVirtualWebHookTypes(device, true).components?.find(
			(item) => item.component === device.component,
		);
		return {
			...component,
			cid: resolveXT1ComponentChannel(device).channel,
			type: device.component.split(':')[0],
			components: null,
		};
	}
	if (
		isBluTRV(device.type) ||
		isVirtualDevice(device.type) ||
		(isXT1Module(device.type) && !isXT1ComponentDevice(device))
	) {
		return getVirtualWebHookTypes(device);
	}
	let inputType: string = '',
		components = null;
	const channelDevice = resolveXT1ComponentChannel(device);
	const cid = channelDevice.channel;
	const deviceSetting = devices_settings.getByRealId(device.id);
	const deviceMode = getComponentByDeviceMode(channelDevice).mode;
	let type = '',
		unit = 'C';
	if (device.category == 'emeter') {
		type = 'em';
		if (isGen2EM(device.type) || (isProThreeEM(device.type) && deviceMode == 'monophase')) {
			//Pro EM | Pro 3EM monophase
			type += '1';
		}
	} else if (device.category == 'power_meter') {
		//PM Mini
		type = 'pm1';
	} else if (isDiffuser(device.type)) {
		//Cury
		type = 'cury';
	} else if (device.category == 'breaker') {
		//Shelly Circuit Breaker
		type = 'cb';
		components = getVoltmeterComponents(device, deviceSetting);
	} else if (device.category == 'light') {
		type = deviceMode == 'white' ? 'light' : deviceMode;
		if (checkInputsMoreThanChannels(device, deviceSetting)) {
			type = `input|${deviceMode || type}`;
			components = getComponents(channelDevice, deviceSetting);
		}
	} else if (device.category === 'module' && deviceMode === 'cct') {
		type = 'cct';
		if (checkInputsMoreThanChannels(device, deviceSetting)) {
			type = `input|cct`;
			components = getComponents(channelDevice, deviceSetting);
		}
	} else if (device.category == 'roller') {
		type = 'cover';
		components = getComponents(channelDevice, deviceSetting);
	} else if (isShellyPresence(device.type)) {
		type = 'presencezone';
		components = getPresenceComponents(deviceSetting);
	} else if (device.category == 'relay' && checkInputsMoreThanChannels(device, deviceSetting)) {
		type = `input|toggle`;
		components = getComponents(channelDevice, deviceSetting);
	} else if (isCamera(device.type)) {
		type = 'camera';
		components = getCameraComponents(deviceSetting);
	}
	if (!components && device.category != 'sensor') {
		if (deviceSetting?.[`input:${cid}`]?.type) {
			let _type = deviceSetting?.[`input:${cid}`].type;
			type += '|input';
			inputType += '|' + (_type == 'switch' ? 'toggle' : _type);
			if (isWallDisplay(device.type)) {
				//Wall Display
				if (_type == 'button') inputType = ''; // Multipush actions on the hardware input are not supported for WallDisplays
				inputType += '|touch';
			}
		}
		if (
			deviceSetting?.[`switch:${cid}`] ||
			/^(SPEM-002CEBEU50)$/.test(device.type) //One output with few channels
		) {
			type += '|switch';
		}
	}
	if (device.category == 'sensor' || isWallDisplay(device.type)) {
		//Wall Display
		if (deviceSetting?.[`smoke:${cid}`]) {
			type = 'smoke';
		}
		if (deviceSetting?.[`flood:${cid}`]) {
			type = 'flood';
		} else if (deviceSetting?.[`temperature:${cid}`]) {
			type += '|temperature';
			if (/^(SNSN-0013A|S3SN-0U12A|S3SN-1U12A)$/.test(device.type)) {
				// +H&T && H&T G3
				unit = deviceSetting?.ht_ui?.temperature_unit || 'C';
			}
			if (deviceSetting?.[`humidity:${cid}`]) {
				type += '|humidity';
			}
		}
	}
	if (deviceSetting?.[`illuminance:${cid}`]) {
		type += '|illuminance';
	}

	const lnmComponents = getLnmComponents(deviceSetting);
	if (lnmComponents.length) {
		type += '|lnm';
		components = components ? [...components, ...lnmComponents] : lnmComponents;
	}

	return {
		cid,
		type: type.replace(/^[|]/, ''),
		inputType: inputType.replace(/^[|]/, ''),
		unit,
		components,
	};
};
